import { createPortal } from 'react-dom';
import { useCallback, useEffect, useState } from 'react';
import { zipSync, type Zippable } from 'fflate';
import { adminApi, ApiError } from '../lib/api';
import type {
  Recording,
  RecordingDetail,
  DecodedTranscript,
  QuestionMarker,
  TemplateKey,
} from '../../shared/types';
import type { InterviewQuestion } from '../../shared/extractInterviewQuestions';
import Eyebrow from './Eyebrow';
import TranscriptDialog from './TranscriptDialog';

interface Props {
  scriptId: string;
  /** Used by the transcript dialog to label per-question segments. Pass
   *  an empty array when the script has no interview questions section
   *  authored — segmentation gracefully falls back to a single block. */
  questions: InterviewQuestion[];
  /** Display fields for the transcript dialog header / download filename. */
  scriptTitle: string;
  clientName: string;
  /** Drives the AI paper-edit "Analyse as" default and the regenerate
   *  dropdown's "script default" label. */
  scriptTemplateKey: TemplateKey;
}

const POLL_INTERVAL_MS = 3000;

/**
 * Read-only list of shoot-day audio takes for a script. Lives in the
 * admin edit page sidebar so anything captured from the shoot view is
 * immediately accessible for review without leaving the script.
 *
 * Each row inlines a native <audio controls> element pointing at the
 * worker's R2 streaming endpoint, plus (Phase 2B) a Transcribe button
 * that hands the take to Whisper and displays the resulting text once
 * the polling loop sees status='transcribed'.
 */
export default function RecordingsPanel({
  scriptId,
  questions,
  scriptTitle,
  clientName,
  scriptTemplateKey,
}: Props) {
  const [recordings, setRecordings] = useState<Recording[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Per-row transcript blobs, keyed by recording id. We fetch the detail
  // payload lazily on first transcribe / first view since the list
  // endpoint deliberately omits transcript_json to keep payloads small.
  const [details, setDetails] = useState<Record<string, RecordingDetail>>({});

  // Which recording is currently being viewed in the modal. The modal
  // pulls everything it needs (recording metadata, decoded transcript,
  // markers, take number) from this id.
  const [activeId, setActiveId] = useState<string | null>(null);

  // Tracks which recording ids we've already auto-triggered a paper-edit
  // for in this session. Without this guard the polling loop would
  // re-POST every 3 seconds while the row sits in 'pending' (between the
  // moment our POST returns 202 and the worker writing 'analysing' to D1
  // and our next poll seeing it).
  const [autoTriggeredIds, setAutoTriggeredIds] = useState<Set<string>>(
    () => new Set(),
  );
  // Failed automatic requests stay excluded for the rest of this mount.
  // Retrying is an explicit action in the transcript dialog; an effect must
  // never turn a persistent worker error into a tight POST loop.
  const [autoTriggerFailedIds, setAutoTriggerFailedIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [bulkDownloading, setBulkDownloading] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await adminApi.listRecordings(scriptId);
      setRecordings(res.recordings);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load recordings.');
    }
  }, [scriptId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Refresh whenever the page regains focus — covers the common flow of
  // tapping back from the shoot view in another tab after recording a
  // new take. Cheap (one GET) and avoids stale lists.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', refresh);
    };
  }, [refresh]);

  // Poll any in-flight transcriptions OR paper-edit analyses. Both run
  // in background waitUntils on the worker, so polling is how we learn
  // about state transitions. Stops as soon as no rows are still in
  // flight on either pipeline.
  useEffect(() => {
    if (!recordings) return;
    const inFlight = recordings.filter(
      (r) => r.status === 'transcribing' || r.paper_edit_status === 'analysing',
    );
    if (inFlight.length === 0) return;

    let cancelled = false;
    const poll = async () => {
      // Fetch each in-flight row's latest state. For a typical session
      // there's only ever one transcribe running at a time, so the loop
      // is in the noise.
      const updated = await Promise.all(
        inFlight.map((r) =>
          adminApi.getRecording(r.id).then(
            (res) => res.recording,
            () => null,
          ),
        ),
      );
      if (cancelled) return;
      let anythingChanged = false;
      const nextDetails = { ...details };
      for (const detail of updated) {
        if (!detail) continue;
        nextDetails[detail.id] = detail;
        if (
          detail.status !== 'transcribing' &&
          detail.paper_edit_status !== 'analysing'
        ) {
          anythingChanged = true;
        }
      }
      setDetails(nextDetails);
      if (anythingChanged) {
        await refresh();
      }
    };
    const handle = window.setInterval(() => {
      void poll();
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [recordings, refresh, details]);

  // Auto-trigger paper-edit generation when a recording finishes
  // transcribing. We watch the recordings list directly (not the
  // detail-fetched map) so this fires regardless of whether the user
  // has opened the dialog. The setAutoTriggeredIds guard prevents
  // re-firing on every poll while the worker is still picking up the
  // request.
  useEffect(() => {
    if (!recordings) return;
    const ready = recordings.filter(
      (r) =>
        r.status === 'transcribed' &&
        r.paper_edit_status === 'pending' &&
        !autoTriggeredIds.has(r.id) &&
        !autoTriggerFailedIds.has(r.id),
    );
    if (ready.length === 0) return;
    const triggered = new Set(autoTriggeredIds);
    for (const rec of ready) {
      triggered.add(rec.id);
      void adminApi
        .regeneratePaperEdit(rec.id, null)
        .then(() => {
          // Optimistically reflect in local state so the row shows
          // 'analysing' immediately rather than waiting another full
          // poll cycle.
          setRecordings((prev) =>
            prev
              ? prev.map((r) =>
                  r.id === rec.id ? { ...r, paper_edit_status: 'analysing' } : r,
                )
              : prev,
          );
        })
        .catch(() => {
          setAutoTriggerFailedIds((failed) => new Set(failed).add(rec.id));
        });
    }
    setAutoTriggeredIds(triggered);
  }, [recordings, autoTriggeredIds, autoTriggerFailedIds]);

  async function handleDelete(rec: Recording) {
    if (!confirm('Delete this recording? It will be removed from the list.')) return;
    setDeletingId(rec.id);
    try {
      await adminApi.deleteRecording(rec.id);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Delete failed.');
    } finally {
      setDeletingId(null);
    }
  }

  async function handleTranscribe(rec: Recording) {
    setError(null);
    try {
      await adminApi.transcribeRecording(rec.id);
      // Optimistically flip the row to 'transcribing' so the UI updates
      // immediately; the polling loop will sync the rest.
      setRecordings((prev) =>
        prev
          ? prev.map((r) =>
              r.id === rec.id ? { ...r, status: 'transcribing' } : r,
            )
          : prev,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Transcribe failed.');
    }
  }

  async function openTranscript(rec: Recording) {
    setActiveId(rec.id);
    // Fetch the detail payload if we don't have it yet (or it's stale —
    // status mismatch usually means we cached an old 'transcribing'
    // shell that's now ready).
    const existing = details[rec.id];
    if (!existing || existing.status !== rec.status) {
      try {
        const res = await adminApi.getRecording(rec.id);
        setDetails((prev) => ({ ...prev, [rec.id]: res.recording }));
      } catch {
        // Modal will show its own loading-failed state if the transcript
        // never arrives.
      }
    }
  }

  // Bundle every take into one ZIP and trigger a single browser
  // download. The previous "trigger each download sequentially" approach
  // ran into modern-browser policy: only the first programmatic download
  // from a page is allowed silently, every subsequent one is blocked
  // until the user explicitly grants per-site permission. A single ZIP
  // dodges that policy entirely.
  //
  // We skip compression (level: 0) because audio is already Opus/AAC —
  // re-compressing wastes CPU for ~0 size benefit. The "Downloading…"
  // status is updated per-file so the user sees progress for >2-take
  // shoots where the fetch loop takes a few seconds.
  async function handleDownloadAll() {
    if (!recordings || recordings.length === 0 || bulkDownloading) return;
    setBulkDownloading(true);
    setError(null);
    try {
      const files: Zippable = {};
      for (let i = 0; i < recordings.length; i++) {
        const rec = recordings[i];
        const ext = rec.mime_type.startsWith('audio/mp4') ? 'm4a' : 'webm';
        // Filenames live inside the ZIP; keep them short and stable.
        const innerName = `Take ${i + 1}.${ext}`;
        const res = await fetch(adminApi.recordingAudioUrl(rec.id), {
          credentials: 'same-origin',
        });
        if (!res.ok) {
          setError(`Take ${i + 1} fetch failed (HTTP ${res.status}).`);
          return;
        }
        const buffer = await res.arrayBuffer();
        files[innerName] = new Uint8Array(buffer);
      }

      const zipped = zipSync(files, { level: 0 });
      // fflate returns a Uint8Array. Wrap as a Blob for the anchor click.
      const blob = new Blob([zipped as BlobPart], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sanitize(clientName)} — ${sanitize(scriptTitle)} — takes.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed.');
    } finally {
      setBulkDownloading(false);
    }
  }

  return (
    <section
      data-print-hide
      className="bg-card border border-line rounded-md px-5 py-4"
    >
      <div className="flex flex-wrap md:flex-nowrap items-center justify-between mb-3 gap-x-2 gap-y-1 md:gap-2">
        <Eyebrow>// Shoot recordings</Eyebrow>
        <div className="flex items-center gap-3">
          {recordings && recordings.length > 0 && (
            <button
              onClick={handleDownloadAll}
              disabled={bulkDownloading}
              className="
                font-mono text-[10px] uppercase tracking-[0.12em] whitespace-nowrap md:whitespace-normal
                text-muted hover:text-fg transition-colors
                disabled:opacity-50
              "
              title={`Bundle all ${recordings.length} take${
                recordings.length === 1 ? '' : 's'
              } into a single ZIP and download`}
            >
              {bulkDownloading
                ? `Zipping ${recordings.length} take${
                    recordings.length === 1 ? '' : 's'
                  }…`
                : '↓ Download all as ZIP'}
            </button>
          )}
          {recordings !== null && (
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
              {recordings.length} take{recordings.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
      </div>

      {error && (
        <p className="text-error text-[13px] mb-3">{error}</p>
      )}

      {recordings === null ? (
        <p className="text-faint text-[12px]">Loading…</p>
      ) : recordings.length === 0 ? (
        <p className="text-muted text-[13px] leading-relaxed">
          No recordings yet. Open the{' '}
          <span className="text-fg font-medium">Shoot view</span> on a phone
          and tap Record to capture an interview take.
        </p>
      ) : (
        <ul className="space-y-3">
          {recordings.map((rec, i) => (
            <RecordingRow
              key={rec.id}
              recording={rec}
              detail={details[rec.id] ?? null}
              takeNumber={i + 1}
              isDeleting={deletingId === rec.id}
              onDelete={() => handleDelete(rec)}
              onTranscribe={() => handleTranscribe(rec)}
              onOpenTranscript={() => openTranscript(rec)}
            />
          ))}
        </ul>
      )}

      {/* Single shared dialog instance keyed off activeId. Driven entirely
          off cached detail blobs — opening it before details arrive shows
          the dialog's own "loading" state. Portal outside the settings/drawer
          so collapsing or crossing a breakpoint cannot hide an open dialog. */}
      {activeId && createPortal(
        <div data-print-hide className="relative z-[70]">
          {(() => {
            const rec = recordings?.find((r) => r.id === activeId);
            const detail = details[activeId];
            if (!rec || !detail || !detail.transcript_json) {
              return (
                <DialogLoadingShell onClose={() => setActiveId(null)} />
              );
            }
            const decoded = decodeTranscript(detail.transcript_json);
            if (!decoded) {
              return (
                <DialogLoadingShell
                  onClose={() => setActiveId(null)}
                  errorMessage="Transcript stored but could not be parsed."
                />
              );
            }
            const markers = parseMarkers(rec.question_markers);
            const takeNumber =
              (recordings?.findIndex((r) => r.id === activeId) ?? 0) + 1;
            return (
              <TranscriptDialog
                open
                onClose={() => setActiveId(null)}
                recording={rec}
                takeNumber={takeNumber}
                transcript={decoded}
                markers={markers}
                questions={questions}
                scriptTitle={scriptTitle}
                clientName={clientName}
                scriptTemplateKey={scriptTemplateKey}
                paperEditStatus={detail.paper_edit_status}
                paperEditJson={detail.paper_edit_json}
                paperEditError={detail.paper_edit_error}
                paperEditTemplateOverride={detail.paper_edit_template_override}
                onDetailRefresh={async () => {
                  try {
                    const res = await adminApi.getRecording(rec.id);
                    setDetails((prev) => ({ ...prev, [rec.id]: res.recording }));
                    return res.recording;
                  } catch {
                    return null;
                  }
                }}
              />
            );
          })()}
        </div>,
        document.body,
      )}
    </section>
  );
}

/** Tiny placeholder dialog used while the transcript payload is in
 *  flight, or when its JSON is unparseable. */
function DialogLoadingShell({
  onClose,
  errorMessage,
}: {
  onClose: () => void;
  errorMessage?: string;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-ink-900/70 backdrop-blur-md z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Transcript"
        onClick={(e) => e.stopPropagation()}
        className="bg-page border border-line rounded-md px-6 py-4 max-w-sm w-full mx-4"
      >
        <div className="flex justify-end mb-2">
          <button
            type="button"
            autoFocus
            onClick={onClose}
            aria-label="Close transcript"
            className="h-8 w-8 rounded text-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
          >
            <span aria-hidden>×</span>
          </button>
        </div>
        <p className="text-fg text-[14px]">
          {errorMessage ?? 'Loading transcript…'}
        </p>
      </div>
    </div>
  );
}

function parseMarkers(json: string): QuestionMarker[] {
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (m): m is QuestionMarker =>
        !!m &&
        typeof (m as QuestionMarker).q === 'number' &&
        typeof (m as QuestionMarker).t === 'number',
    );
  } catch {
    return [];
  }
}

function RecordingRow({
  recording,
  detail,
  takeNumber,
  isDeleting,
  onDelete,
  onTranscribe,
  onOpenTranscript,
}: {
  recording: Recording;
  detail: RecordingDetail | null;
  takeNumber: number;
  isDeleting: boolean;
  onDelete: () => void;
  onTranscribe: () => void;
  onOpenTranscript: () => void;
}) {
  return (
    <li className="border-t border-line pt-3 first:border-t-0 first:pt-0">
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
            Take {takeNumber}
          </span>
          <span className="text-faint/60">·</span>
          <span className="font-mono text-[10px] tabular-nums text-faint">
            {formatDuration(recording.duration_ms)}
          </span>
          <RecordingStatusPill status={recording.status} />
        </div>
        <button
          onClick={onDelete}
          disabled={isDeleting}
          className="
            font-mono text-[10px] uppercase tracking-[0.12em]
            text-faint hover:text-accent transition-colors
            disabled:opacity-50
          "
        >
          {isDeleting ? 'Deleting…' : 'Delete'}
        </button>
      </div>
      <p className="text-faint text-[11px] mb-2">
        Recorded {formatRecordedAt(recording.recorded_at)}
      </p>
      <audio
        controls
        preload="none"
        src={adminApi.recordingAudioUrl(recording.id)}
        className="w-full h-8"
      />

      {/* Transcription controls live below the audio. Visually muted —
          they aren't the primary purpose of the row, just a useful add. */}
      <div className="mt-2 flex items-center justify-between gap-2">
        {recording.status === 'recorded' && (
          <button
            onClick={onTranscribe}
            className="
              font-mono text-[10px] uppercase tracking-[0.12em]
              text-muted hover:text-fg transition-colors
            "
          >
            ↑ Transcribe with Whisper
          </button>
        )}
        {recording.status === 'transcribing' && (
          <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint flex items-center gap-1.5">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-warning animate-pulse" />
            Transcribing — this can take a minute
          </span>
        )}
        {recording.status === 'transcribed' && (
          <button
            onClick={onOpenTranscript}
            className="
              font-mono text-[10px] uppercase tracking-[0.12em]
              text-muted hover:text-fg transition-colors
            "
          >
            ▸ View transcript &amp; paper edit
          </button>
        )}
        {recording.status === 'failed' && (
          <button
            onClick={onTranscribe}
            className="
              font-mono text-[10px] uppercase tracking-[0.12em]
              text-error hover:opacity-80 transition-opacity
            "
            title={detail?.transcript_error ?? undefined}
          >
            ↻ Transcription failed — retry
          </button>
        )}
      </div>

      {recording.status === 'failed' && detail?.transcript_error && (
        <p className="mt-2 text-[11px] text-error/80 leading-relaxed">
          {detail.transcript_error}
        </p>
      )}
    </li>
  );
}

function decodeTranscript(json: string | null): DecodedTranscript | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json) as DecodedTranscript;
    if (typeof parsed.text !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

function RecordingStatusPill({
  status,
}: {
  status: Recording['status'];
}) {
  if (status === 'recorded') return null;
  const map: Record<
    Exclude<Recording['status'], 'recorded'>,
    { label: string; cls: string }
  > = {
    transcribing: {
      label: 'Transcribing',
      cls: 'text-warning border-warning/40 bg-warning/5',
    },
    transcribed: {
      label: 'Transcribed',
      cls: 'text-success border-success/40 bg-success/5',
    },
    failed: {
      label: 'Failed',
      cls: 'text-error border-error/40 bg-error/5',
    },
  };
  const v = map[status];
  return (
    <span
      className={
        'font-mono text-[9px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded border ' +
        v.cls
      }
    >
      {v.label}
    </span>
  );
}

/** Strip filesystem-hostile characters from a string so it's safe to use
 *  as a download filename across Windows, macOS, and iOS. */
function sanitize(s: string): string {
  return s.replace(/[\\/:*?"<>|]/g, '').trim() || 'Recording';
}

function formatDuration(ms: number | null): string {
  if (ms === null) return '—:—';
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

function formatRecordedAt(ms: number): string {
  const date = new Date(ms);
  const now = Date.now();
  const diff = now - ms;
  // Within 24h: show "2h ago" for quick scanning. Older: full date so
  // editors hunting an interview from a given week have something to
  // anchor on.
  const minutes = Math.round(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(diff / 3_600_000);
  if (hours < 24) return `${hours}h ago`;
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}
